import React from "react";
import { User, Headphones, ArrowDown } from "lucide-react";
import VisitorMockup from "./VisitorMockup";
import DashboardMockup from "./DashboardMockup";
import ScaledFrame from "./ScaledFrame";

// Intrinsic mockup sizes (must match the fixed dimensions used inside each mockup)
const MOCKUP_W = 560;
const MOCKUP_H = 720;

const VisitorPill = () => (
  <span className="inline-flex items-center gap-2 bg-blue-600 text-white text-xs sm:text-sm font-semibold rounded-full px-4 py-2 shadow-md shadow-blue-600/25">
    <User className="w-4 h-4" />
    Visitor on Your Website
  </span>
);

const DashboardPill = () => (
  <span className="inline-flex items-center gap-2 bg-green-500 text-white text-xs sm:text-sm font-semibold rounded-full px-4 py-2 shadow-md shadow-green-500/25">
    <Headphones className="w-4 h-4" />
    Live Agent Dashboard
  </span>
);

const MockupsSection = () => {
  return (
    <section
      id="how-it-works"
      className="relative pb-14 sm:pb-24 pt-4 sm:pt-8"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* ----- Desktop / Large layout ----- */}
        <div className="hidden lg:block animate-fade-in-up">
          <div className="grid grid-cols-[1fr_auto_1fr] items-start gap-4">
            <div className="flex justify-center">
              <VisitorPill />
            </div>
            <div />
            <div className="flex justify-center">
              <DashboardPill />
            </div>
          </div>

          <div className="mt-6 grid grid-cols-[1fr_auto_1fr] items-center gap-4">
            <div className="w-full max-w-[560px] ml-auto mr-0">
              <ScaledFrame width={MOCKUP_W} height={MOCKUP_H}>
                <VisitorMockup />
              </ScaledFrame>
            </div>

            <div className="flex flex-col items-center justify-center">
              <p className="text-xs italic text-slate-500 text-center leading-snug max-w-[110px] mb-2">
                Visitor starts a chat on your website
              </p>
              <svg width="90" height="22" viewBox="0 0 90 22" fill="none">
                <path
                  d="M2 11 H78"
                  stroke="#60a5fa"
                  strokeWidth="2"
                  strokeLinecap="round"
                  className="animate-dash"
                />
                <path
                  d="M76 4 L86 11 L76 18"
                  stroke="#2563eb"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                />
              </svg>
            </div>

            <div className="w-full max-w-[560px] mr-auto ml-0">
              <ScaledFrame width={MOCKUP_W} height={MOCKUP_H}>
                <DashboardMockup />
              </ScaledFrame>
            </div>
          </div>
        </div>

        {/* ----- Mobile / Small layout ----- */}
        <div className="lg:hidden flex flex-col gap-4 animate-fade-in-up">
          {/* Visitor block */}
          <div className="flex justify-center">
            <VisitorPill />
          </div>
          <div className="w-full max-w-[560px] mx-auto">
            <ScaledFrame width={MOCKUP_W} height={MOCKUP_H}>
              <VisitorMockup />
            </ScaledFrame>
          </div>

          {/* Connector */}
          <div className="flex flex-col items-center py-1">
            <p className="text-[11px] italic text-slate-500">
              Visitor starts a chat
            </p>
            <ArrowDown className="w-4 h-4 text-blue-500 mt-1 animate-pulse-dot" />
          </div>

          {/* Dashboard block */}
          <div className="flex justify-center">
            <DashboardPill />
          </div>
          <div className="w-full max-w-[560px] mx-auto">
            <ScaledFrame width={MOCKUP_W} height={MOCKUP_H}>
              <DashboardMockup />
            </ScaledFrame>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MockupsSection;
